import { NgClass } from "@angular/common";
import { Component, ElementRef, Input, OnDestroy, ViewChild } from "@angular/core";
import { ControlContainer, FormsModule, NgModelGroup, ReactiveFormsModule } from "@angular/forms";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { TruncateTextComponent } from "@tylertech/common-components-module";
import { PopoverComponent } from "@tylertech/forge";
import { ForgeButtonModule, ForgeCardModule, ForgeCheckboxProxyModule, ForgeCircularProgressModule, ForgeIconButtonModule, ForgeIconModule, ForgeListItemModule, ForgeListModule, ForgePopoverProxyModule, ForgeScaffoldModule, ForgeTextFieldModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { MetadataKey, RepositoryFormData } from "../../../../interfaces";
import { MAX_RECORDS_RENDERED, MetadataInputSelectComponent } from "./metadata-input-select.component";
import { KeyValuePair } from "@tylertech/tyler-api-types";
import { takeUntil } from "rxjs";

@Component({
    selector: "metadata-input-multi-select",
    templateUrl: "./metadata-input-multi-select.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    imports: [
        ClassDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        ForgeCardModule,
        ForgeCheckboxProxyModule,
        ForgeCircularProgressModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeListItemModule,
        ForgeListModule,
        ForgePopoverProxyModule,
        ForgeScaffoldModule,
        ForgeTextFieldModule,
        ForgeTooltipModule,
        FormsModule,
        NgClass,
        ReactiveFormsModule,
        TruncateTextComponent,
    ]
})
export class MetadataInputMultiSelectComponent extends MetadataInputSelectComponent implements OnDestroy
{
    @Input() datacontroller: RepositoryFormData;

    @ViewChild("popover") popover: PopoverComponent;
    @ViewChild("popupContent") popupContent: any;
    @ViewChild("searchFilter") searchFilter: ElementRef;

    opening: boolean = false;

    multiSelectListCard: Element;

    ascendingSort = true;

    private multiScrollHandler = (event: Event): void => this.triggerScroll(event);

    get multiSelectListSelectId(): string
    {
        return `${this.popupId}MultiSelectListCard`;
    }

    get selectedCount(): string
    {
        const selectCount = (this.selected as MetadataKey[])?.length ?? 0;

        if (selectCount === 1)
        {
            const selected = this._options.filter(x => x.Key === this.selected[0]);
            return selected[0].Value;
        }
        if (selectCount > 1)
        {
            return `${selectCount} options selected`;
        }

        return "";
    }

    override ngOnDestroy(): void
    {
        if (this.popover?.open)
            this.modelForTracking.$recalculating = false;

        this.multiSelectListCard?.removeEventListener("scroll", this.multiScrollHandler);
        this.popover = null;
        this.popupContent = null;
        this.searchFilter = null;
        super.ngOnDestroy();
    }

    override finalizeSelect(): void
    {
        this.sortOptions();
    }

    override initializeListeners(): void
    {
        const self = this;
        self.$metadataInputSelectService.onMultiSelectChange.pipe(takeUntil(this.destructor$))
            .subscribe(id =>
            {
                if (id === `multi-select-${self.popupId}` && !Utilities.objectsEqual(self.selected ? (<MetadataKey[]>self.selected).sort() : self.selected, self.model ? (<MetadataKey[]>self.model).sort() : self.model))
                    self.fireValueChanged(self.selected);
                self.optionSearch = "";
            });

        self.$metadataInputSelectService.onMultiSelectScroll.pipe(takeUntil(this.destructor$))
            .subscribe(e =>
            {
                if (e.name === `multi-select-${self.popupId}`)
                    self.infiniteScroll(e.event);
            });
    }

    override selectItem(item: MetadataKey): void
    {
        if (this.isViewOnly)
            return;
        const index = (this.selected as MetadataKey[]).indexOf(item);
        if (index < 0)
            (this.selected as MetadataKey[]).push(item);
        else
            (this.selected as MetadataKey[]).splice(index, 1);
    }

    override setSelected(value: MetadataKey | MetadataKey[]): void
    {
        this._selected = value;
    }

    override applySort(unsorted: KeyValuePair<MetadataKey, string>[]): KeyValuePair<MetadataKey, string>[]
    {
        return unsorted;
    }

    override getSelectList(): KeyValuePair<MetadataKey, string>[]
    {
        if (this.optionSearch.length > 0)
        {
            return this._options
                .filter(x => x.Value.toLowerCase()
                    .includes(this.optionSearch.toLowerCase()))
                .slice(0, MAX_RECORDS_RENDERED * this.loadMoreCount);
        }
        else
        {
            return this._options
                .slice(0, MAX_RECORDS_RENDERED * this.loadMoreCount);
        }
    }

    override shouldLoadMore(): boolean
    {
        return this._options.filter(x => x.Value.toLowerCase()
            .includes(this.optionSearch.toLowerCase())).length > MAX_RECORDS_RENDERED * this.loadMoreCount;
    }

    selectAllText(deselect: boolean): string
    {
        const appendFiltered = this.optionSearch.length > 0 ? " (filtered)" : "";
        return deselect ? `Deselect all${appendFiltered}` : `Select all${appendFiltered}`;
    }

    sortOptions(ascending: boolean = true): void
    {
        this._options = this._options
            .sort((a, b) =>
            {
                const aChecked = this.isItemChecked(a.Key);
                const bChecked = this.isItemChecked(b.Key);

                if (bChecked && !aChecked)
                {
                    return 1;
                }
                if (!bChecked && aChecked)
                {
                    return -1;
                }

                const comparison = a.Value.localeCompare(b.Value);
                return ascending ? comparison : -comparison;
            });
    }

    isItemChecked(item: MetadataKey): boolean
    {
        return (this.selected as MetadataKey[])?.indexOf(item) >= 0;
    }

    openPopup(open: boolean): void
    {
        this.opening = true;
        if (open)
            this.popover.open = true;
        this.opening = false;
        const self = this;
        requestAnimationFrame(() =>
        {
            const id = `mlt-${this.popupId}`;

            if (self.forCellEdit)
            {
                return;
            }
            const popup: any = document.querySelector(`#${this.getPopupId(id)}`);
            const popupTarget: any = document.querySelector(`#${id}`);
            const targetBounds = popupTarget.getBoundingClientRect();
            if (popup)
            {
                popup.style.width = `${targetBounds.width + 30}px`;
                this.searchFilter.nativeElement.focus();

                if (!this.multiSelectListCard)
                {
                    this.multiSelectListCard = document.querySelector(`#${this.popupId}MultiSelectListCard`).querySelector("forge-list");
                    if (!Utilities.IsNullOrUndefined(this.multiSelectListCard))
                    {
                        this.multiSelectListCard.addEventListener("scroll", this.multiScrollHandler);
                    }
                }
            }
        });
    }

    triggerScroll(event): void
    {
        this.$metadataInputSelectService.fireMultiSelectScrollListener(this.popupContent.nativeElement.id, event);
    }

    textContainerKeydown($event: any): void
    {
        if ($event.key === "ArrowDown")
        {
            this.openPopup(true);
            $event.preventDefault();
            $event.stopPropagation();
        }

        if ($event.code === "NumpadAdd")
        {
            $event.preventDefault();
            this.onKeyDown($event);
        }
    }

    closePopup(): void
    {
        if (!this.opening)
        {
            this.$metadataInputSelectService.fireMultiSelectChangeListener(`multi-select-${this.popupId}`);
        }

        const target: any = document.querySelector(`#${this.getIdFromPopupId(this.popupContent.nativeElement.id)}`);
        target.focus();

        if (this.popover.open)
        {
            this.popover.open = false;
        }

        this.multiSelectListCard?.removeEventListener("scroll", this.multiScrollHandler);
        this.multiSelectListCard = null;

        this.sortOptions(this.ascendingSort);
        this.maybeRunSave();
        this.maybeRunRecalculate();
    }

    trapTab(e: KeyboardEvent): void
    {
        if (e.key === "Tab" && e.shiftKey)
        {
            this.closePopup();
        }
    }

    sort(): void
    {
        this.ascendingSort = !this.ascendingSort;
        this.sortOptions(this.ascendingSort);
    }

    get isAscendingSort(): boolean
    {
        return this.ascendingSort;
    }

    closeButtonKeyDown(e: KeyboardEvent): void
    {
        if (e.key === "Tab" && !e.shiftKey)
        {
            e.preventDefault();
            this.closePopup();
        }
    }

    private getIdFromPopupId(popupId): any
    {
        if (popupId.includes("multi-select-"))
            return popupId.replace("multi-select-", "mlt-");
        else return popupId;
    }

    getPopupId(id): any
    {
        if (id.includes("mlt-")) return id.replace("mlt-", "multi-select-");
        else return id;
    }

    togglePopover($event: any): void
    {
        if ($event.detail?.newState === "open")
            this.modelForTracking.$recalculating = true;
        if ($event.detail?.newState === "closed")
            this.closePopup();
    }

    maybeRunSave(): void
    {
        this.modelForTracking.$recalculating = false;
        if (this.modelForTracking.$saveAfterRecalculate)
            this.$metadataEventService.fireSaveAfterRecalculateListener();
    }

    maybeRunRecalculate(): void
    {
        if (!this.modelForTracking.$recalculateAfterRecalculate)
            return;
        this.$metadataEventService.fireRecalculateWorkingListeners(
            {
                ShowLoading: false,
                Recalculating: false,
            },
            false);
    }

    get modelForTracking(): any
    {
        if (this.datacontroller && this.datacontroller.OtherEntities && this.datacontroller.OtherEntities[this.metadataModel.$$TypeName] && this.datacontroller.OtherEntities[this.metadataModel.$$TypeName].length > 0)
            return this.datacontroller.Entity;
        return this.metadataModel;
    }
}
